import { Directive, Input, TemplateRef, ViewContainerRef, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';

/**
 * Structural directive for role-based template rendering
 * 
 * @example
 * // Show content only for admins
 * <div *appHasRole="'admin'">Admin-only content</div>
 * 
 * @example
 * // Show content for users with any of the specified roles
 * <div *appHasRole="['admin', 'superadmin']">Admin or Superadmin content</div>
 */
@Directive({
  selector: '[appHasRole]',
  standalone: true
})
export class HasRoleDirective implements OnDestroy {
  private destroy$ = new Subject<void>();
  private hasView = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private authService: AuthService
  ) {}

  @Input() set appHasRole(roles: string | string[]) {
    const roleArray = Array.isArray(roles) ? roles : [roles];
    
    this.authService.hasAnyRole$(roleArray)
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasRole => {
        if (hasRole && !this.hasView) {
          this.viewContainer.createEmbeddedView(this.templateRef);
          this.hasView = true;
        } else if (!hasRole && this.hasView) {
          this.viewContainer.clear();
          this.hasView = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}